import Axios, { CancelTokenSource } from 'axios';
import { action, Action, thunk, Thunk, thunkOn, ThunkOn } from 'easy-peasy';
import ResourceQuery from 'Features/Query/ResourceQuery';
import { TFilter } from 'Models/AppModels';
import ArticleModel from 'Models/Article';
import ResourceModel from 'Models/Resource';
import { TResource } from 'Models/Resource/@types';
import { parseResource } from 'Models/Resource/resourceParsers';
// eslint-disable-next-line import/no-cycle
import { TRootStore } from 'Stores';

export interface ReadState {
  articleDetail?: TResource;
  setArticleDetail: Action<ReadState, TResource | undefined>;
  fetchArticleDetail: Thunk<ReadState, { id: string, filters?: TFilter }, null, TRootStore>;
  loadingDetails: boolean;
  setLoadingDetails: Action<ReadState, boolean>;

  readList: TResource[];
  fetchReadList: Thunk<ReadState, ResourceQuery, null, TRootStore>;
  setReadList: Action<ReadState, { readList: TResource[] }>;

  loading: boolean;
  setLoading: Action<ReadState, boolean>;

  total: number;
  setTotal: Action<ReadState, number>;

  query: ResourceQuery;
  setQuery: Action<ReadState, ResourceQuery>;
  onQueryChange: ThunkOn<ReadState, Record<string, unknown>, TRootStore>;
}

let cancelToken: CancelTokenSource | undefined;

const ReadStore: ReadState = {
  loadingDetails: false,
  readList: [],
  total: 0,
  loading: false,
  query: new ResourceQuery({}, { page: 1 }),
  setArticleDetail: action((state, payload) => {
    state.articleDetail = payload;
  }),
  setLoadingDetails: action((state, payload) => {
    state.loadingDetails = payload;
  }),

  setReadList: action((state, { readList }) => {
    state.readList = readList;
  }),
  setTotal: action((state, payload) => {
    state.total = payload;
  }),
  setQuery: action((state, payload) => {
    state.query = payload;
  }),
  setLoading: action((state, payload) => {
    state.loading = payload;
  }),
  fetchReadList: thunk(async (actions, query, { getStoreState }) => {
    actions.setLoading(true);
    const { community } = getStoreState().CommunityStore;
    if (!community) return;

    query.communityId = community?.id ?? '';
    query.docType = 'Article';

    if (cancelToken) {
      cancelToken.cancel('Operation canceled due to new request.');
    }
    cancelToken = Axios.CancelToken.source();

    const res = await ResourceModel.search(
      community?.id ?? '',
      '',
      {
        Article: {
          include: 'tags',
        },
        ...query.searchFilter,
      },
      { cancelToken: cancelToken.token }
    );

    if (res) {
      const readList: TResource[] = [...res.results.map((res) => parseResource(res.hit))];
      actions.setTotal(res.total);
      actions.setReadList({ readList });
    }
    actions.setLoading(false);
  }),
  fetchArticleDetail: thunk(async (actions, { id, filters = {} }) => {
    try {
      actions.setLoadingDetails(true);
      const res = await ArticleModel.getArticle(id, filters);
      actions.setArticleDetail(res);
    } catch (error) {
      console.error(error)
    } finally {
      actions.setLoadingDetails(false);
    }
  }),
  onQueryChange: thunkOn(
    (actions) => actions.setQuery,
    async (actions, { payload }) => {
      actions.fetchReadList(payload);
    }
  ),
};

export default ReadStore;
